import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Briefcase, Clock, Star, Headphones } from "lucide-react";

interface Stat {
  id: number;
  icon: typeof Briefcase;
  value: number;
  suffix: string;
  label: string;
  duration: number;
  decimal?: boolean;
}

const stats: Stat[] = [
  {
    id: 1,
    icon: Briefcase,
    value: 150,
    suffix: "+",
    label: "Projects Completed",
    duration: 2.5,
  },
  {
    id: 2,
    icon: Clock,
    value: 18,
    suffix: "+",
    label: "Years of Excellence",
    duration: 2,
  },
  {
    id: 3,
    icon: Star,
    value: 4.8,
    suffix: "/5",
    label: "Average Rating",
    duration: 2,
    decimal: true,
  },
  {
    id: 4,
    icon: Headphones,
    value: 24,
    suffix: "/7",
    label: "Support Available",
    duration: 1.5,
  },
];

function AnimatedCounter({ 
  value, 
  suffix, 
  duration,
  decimal = false
}: { 
  value: number; 
  suffix: string; 
  duration: number;
  decimal?: boolean;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!isInView) return;
    
    if (prefersReducedMotion) {
      setCount(value);
      return;
    }

    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = decimal 
        ? Math.round(easeOutQuart * value * 10) / 10
        : Math.floor(easeOutQuart * value);
      
      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(value);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, value, duration, prefersReducedMotion, decimal]);

  return (
    <div ref={ref} className="text-5xl md:text-6xl lg:text-7xl font-heading font-extrabold gradient-text">
      {decimal ? count.toFixed(1) : count}
      <span className="gradient-text">{suffix}</span>
    </div>
  );
}

export default function Stats() {
  const prefersReducedMotion = useReducedMotion();
  
  const containerVariants = {
    hidden: { opacity: prefersReducedMotion ? 1 : 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion ? { duration: 0 } : {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: prefersReducedMotion ? 1 : 0, scale: prefersReducedMotion ? 1 : 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: prefersReducedMotion ? { duration: 0 } : {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" data-testid="section-stats">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#00d9ff]/10 via-[#ffd700]/10 to-[#00d9ff]/10 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold gradient-text mb-4" data-testid="text-stats-title">
            Proven Track Record
          </h2>
          <p className="text-lg md:text-xl text-[#e0e0e0]/70 max-w-2xl mx-auto" data-testid="text-stats-subtitle">
            Numbers that speak louder than words - our commitment to excellence in every project
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12"
        >
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.id}
                variants={itemVariants}
                className="text-center glass-card p-8 hover-lift hover-glow-cyan transition-all duration-500"
                data-testid={`stat-item-${stat.id}`}
              >
                <div className="mb-4 flex justify-center">
                  <div className="bg-[#00d9ff]/20 backdrop-blur-md p-4 rounded-xl border border-[#00d9ff]/30">
                    <Icon className="h-8 w-8 md:h-10 md:w-10 text-[#00d9ff]" data-testid={`icon-stat-${stat.id}`} />
                  </div>
                </div>
                
                <AnimatedCounter 
                  value={stat.value} 
                  suffix={stat.suffix}
                  duration={stat.duration}
                  decimal={stat.decimal}
                />
                
                <p 
                  className="text-base md:text-lg font-medium text-[#e0e0e0]/80 mt-3"
                  data-testid={`text-stat-label-${stat.id}`}
                >
                  {stat.label}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.8, duration: 0.6 }}
          className="text-center mt-12 md:mt-16"
        >
          <p className="text-[#e0e0e0]/60 text-sm md:text-base" data-testid="text-stats-cta">
            Join hundreds of satisfied homeowners who trusted HHS Build with their renovation projects
          </p>
        </motion.div>
      </div>
    </section>
  );
}
